import { useEffect, useState, Suspense } from 'react'
import { useParams } from 'react-router-dom'
import { ModuleMdxProvider } from '../components/ModuleMdxProvider'
import moduleImports from '../generated/module-imports'
import contentIndex from '@content/content-index.json'

const STAGE_LABELS: Record<string, string> = {
  foundation: 'Foundation',
  core: 'Core',
  advanced: 'Advanced',
  enrichment: 'Enrichment',
}

interface IndexedModule {
  id: string
  title: string
  crew: string
  teams: string[]
  stage: string
  wiifm: string
}

export default function ModulePrintPage() {
  const { moduleId } = useParams()
  const [mod, setMod] = useState<IndexedModule | null>(null)
  const [MdxComponent, setMdxComponent] = useState<React.ComponentType | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    document.body.classList.add('print-mode')
    return () => document.body.classList.remove('print-mode')
  }, [])

  useEffect(() => {
    async function load() {
      if (!moduleId) return
      const found = (contentIndex as { modules: IndexedModule[] }).modules.find(
        (m) => m.id === moduleId,
      )
      if (!found) {
        setError(`Module not found in content-index.json: ${moduleId}`)
        return
      }
      setMod(found)
      const importFn = (moduleImports as Record<string, () => Promise<{ default: React.ComponentType }>>)[moduleId]
      if (!importFn) {
        setError(`No MDX import registered for module: ${moduleId}`)
        return
      }
      try {
        const mdxModule = await importFn()
        setMdxComponent(() => mdxModule.default)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load MDX')
      }
    }
    load()
  }, [moduleId])

  if (error) {
    return <div className="print-error">{error}</div>
  }
  if (!mod || !MdxComponent) {
    return <div className="print-loading">Loading…</div>
  }

  return (
    <article className="print-module" data-module-id={mod.id}>
      <header className="print-header">
        <h1>{mod.title}</h1>
        <p className="print-meta">
          {mod.crew} · {mod.teams.join(' / ')} · {STAGE_LABELS[mod.stage] ?? mod.stage}
        </p>
        <p className="print-wiifm">{mod.wiifm}</p>
      </header>
      <ModuleMdxProvider>
        <Suspense fallback={<div>Loading…</div>}>
          <MdxComponent />
        </Suspense>
      </ModuleMdxProvider>
      <footer className="print-footer">
        <p>{mod.id} · Generated {new Date().toISOString().slice(0, 10)}</p>
      </footer>
    </article>
  )
}
